import React, { useEffect, useRef, useState } from "react";
import styles from "./Magnifier.module.css";

function Magnifier({
  src,
  alt = "",
  zoom = 2,
  size = 160,
  imageClassName,
  children,
  imageRef,
  style,
}) {
  const imgRef = useRef(null);
  const [natural, setNatural] = useState({ w: 0, h: 0 });
  const [lens, setLens] = useState({ x: 0, y: 0, show: false });

  const setRefs = (el) => {
    imgRef.current = el;
    if (typeof imageRef === "function") {
      imageRef(el);
    } else if (imageRef) {
      imageRef.current = el;
    }
  };

  useEffect(() => {
    setLens((prev) => ({ ...prev, show: false }));
  }, [src]);

  const handleLoad = () => {
    if (imgRef.current) {
      setNatural({
        w: imgRef.current.naturalWidth,
        h: imgRef.current.naturalHeight,
      });
    }
  };

  const handleMove = (e) => {
    const img = imgRef.current;
    if (!img || !natural.w || !natural.h) return;

    const rect = img.getBoundingClientRect();
    const relX = e.clientX - rect.left;
    const relY = e.clientY - rect.top;

    const half = size / 2;
    const x = Math.max(half, Math.min(rect.width - half, relX));
    const y = Math.max(half, Math.min(rect.height - half, relY));

    setLens({ x, y, show: true });
  };

  const handleLeave = () => setLens((prev) => ({ ...prev, show: false }));

  const getBackgroundStyles = () => {
    if (!imgRef.current || !natural.w || !natural.h) return {};
    
    const img = imgRef.current;
    const rect = img.getBoundingClientRect();
    const displayedWidth = rect.width;
    const displayedHeight = rect.height;
    
    if (displayedWidth === 0 || displayedHeight === 0) return {};
    
    const scaleX = natural.w / displayedWidth;
    const scaleY = natural.h / displayedHeight;
    
    const bgWidth = natural.w * zoom;
    const bgHeight = natural.h * zoom;
    
    const sourceX = (lens.x / displayedWidth) * natural.w;
    const sourceY = (lens.y / displayedHeight) * natural.h;
    
    const bgX = -(sourceX * zoom - size / 2);
    const bgY = -(sourceY * zoom - size / 2);
    
    return {
      backgroundSize: `${bgWidth}px ${bgHeight}px`,
      backgroundPosition: `${bgX}px ${bgY}px`,
    };
  };

  return (
    <div
      className={styles.wrapper}
      onMouseMove={handleMove}
      onMouseEnter={handleMove}
      onMouseLeave={handleLeave}
      style={style}
    >
      <img
        ref={setRefs}
        src={src}
        alt={alt}
        className={`${styles.image} ${imageClassName || ""}`.trim()}
        onLoad={handleLoad}
      />
      {children}
      {lens.show && natural.w > 0 && (
        <div
          className={styles.lens}
          style={{
            width: size,
            height: size,
            left: lens.x - size / 2,
            top: lens.y - size / 2,
            backgroundImage: `url(${src})`,
            ...getBackgroundStyles(),
          }}
        />
      )}
    </div>
  );
}

export default Magnifier;
